import type {
  AdminConnectivityView,
  AuthSessionView,
  ChangePasswordInput,
  CleanupJobView,
  CreateUserInput,
  LoginInput,
  MysqlConfigurationInput,
  PocCycleInput,
  PocTenantView,
  PocTimelineView,
  PreImplementationTenantListView,
  ResetPasswordInput,
  TenantClassification,
  TenantListView,
  TenantView,
  TrialStateInput,
  TrialTenantView,
  UpdateUserInput,
  UserView,
} from "@ubisafe/shared";

export class ApiClientError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly fieldErrors?: Record<string, string[]>,
  ) {
    super(message);
    this.name = "ApiClientError";
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(path, {
    ...init,
    credentials: "include",
    headers: {
      ...(init.body ? { "Content-Type": "application/json" } : {}),
      ...init.headers,
    },
  });

  if (!response.ok) {
    const fallback = { error: { code: "HTTP_ERROR", message: "Não foi possível concluir a solicitação." } };
    const payload = (await response.json().catch(() => fallback)) as {
      error?: { code?: string; message?: string; fieldErrors?: Record<string, string[]> };
    };
    throw new ApiClientError(
      response.status,
      payload.error?.code ?? "HTTP_ERROR",
      payload.error?.message ?? fallback.error.message,
      payload.error?.fieldErrors,
    );
  }

  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

export const api = {
  getSession: () => request<AuthSessionView>("/api/auth/me"),
  login: (input: LoginInput) =>
    request<AuthSessionView>("/api/auth/login", { method: "POST", body: JSON.stringify(input) }),
  logout: () => request<void>("/api/auth/logout", { method: "POST" }),
  changePassword: (input: ChangePasswordInput) =>
    request<AuthSessionView>("/api/auth/change-password", {
      method: "POST",
      body: JSON.stringify(input),
    }),
  forgotPassword: (prefix: string) =>
    request<{ message: string }>("/api/auth/forgot-password", {
      method: "POST",
      body: JSON.stringify({ prefix }),
    }),
  resetPassword: (input: ResetPasswordInput) =>
    request<void>("/api/auth/reset-password", { method: "POST", body: JSON.stringify(input) }),
  listUsers: () => request<{ users: UserView[] }>("/api/users"),
  createUser: (input: CreateUserInput) =>
    request<UserView>("/api/users", { method: "POST", body: JSON.stringify(input) }),
  updateUser: (userId: string, input: UpdateUserInput) =>
    request<UserView>(`/api/users/${encodeURIComponent(userId)}`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  listClients: () => request<TenantListView>("/api/tenants/clients"),
  listTenantCatalog: () => request<TenantListView>("/api/tenants/catalog"),
  updateTenantClassification: (identifier: string, classification: TenantClassification) =>
    request<TenantView>(`/api/tenants/${encodeURIComponent(identifier)}/classification`, {
      method: "PATCH",
      body: JSON.stringify({ classification }),
    }),
  listPreImplementationTenants: () =>
    request<PreImplementationTenantListView>("/api/tenants/pre-implementation"),
  startPocCycle: (identifier: string, input: PocCycleInput) =>
    request<PocTenantView>(`/api/tenants/${encodeURIComponent(identifier)}/poc-cycles`, {
      method: "POST",
      body: JSON.stringify(input),
    }),
  editActivePocCycle: (identifier: string, input: PocCycleInput) =>
    request<PocTenantView>(`/api/tenants/${encodeURIComponent(identifier)}/poc-cycles/active`, {
      method: "PATCH",
      body: JSON.stringify(input),
    }),
  getPocTimeline: (identifier: string) =>
    request<PocTimelineView>(`/api/tenants/${encodeURIComponent(identifier)}/poc-cycles/history`),
  createCleanupJob: (identifier: string, confirmationIdentifier: string) =>
    request<CleanupJobView>(`/api/tenants/${encodeURIComponent(identifier)}/cleanup-jobs`, {
      method: "POST",
      body: JSON.stringify({ confirmationIdentifier }),
    }),
  getCleanupJob: (identifier: string, jobId: string) =>
    request<CleanupJobView>(
      `/api/tenants/${encodeURIComponent(identifier)}/cleanup-jobs/${encodeURIComponent(jobId)}`,
    ),
  confirmCleanupStep: (identifier: string, jobId: string, stage: string, expectedCount: number) =>
    request<CleanupJobView>(
      `/api/tenants/${encodeURIComponent(identifier)}/cleanup-jobs/${encodeURIComponent(jobId)}/steps/${encodeURIComponent(stage)}/confirm`,
      { method: "POST", body: JSON.stringify({ expectedCount }) },
    ),
  retryCleanupItem: (identifier: string, jobId: string, itemId: string) =>
    request<CleanupJobView>(
      `/api/tenants/${encodeURIComponent(identifier)}/cleanup-jobs/${encodeURIComponent(jobId)}/items/${encodeURIComponent(itemId)}/retry`,
      { method: "POST" },
    ),
  ignoreCleanupItem: (identifier: string, jobId: string, itemId: string) =>
    request<CleanupJobView>(
      `/api/tenants/${encodeURIComponent(identifier)}/cleanup-jobs/${encodeURIComponent(jobId)}/items/${encodeURIComponent(itemId)}/ignore`,
      { method: "POST" },
    ),
  markCleanupAsClean: (
    identifier: string,
    jobId: string,
    confirmationIdentifier: string,
  ) =>
    request<CleanupJobView>(
      `/api/tenants/${encodeURIComponent(identifier)}/cleanup-jobs/${encodeURIComponent(jobId)}/mark-clean`,
      {
        method: "POST",
        body: JSON.stringify({ confirmationIdentifier, acknowledgePending: true }),
      },
    ),
  retryCleanupJob: (identifier: string, jobId: string) =>
    request<CleanupJobView>(
      `/api/tenants/${encodeURIComponent(identifier)}/cleanup-jobs/${encodeURIComponent(jobId)}/retry`,
      { method: "POST" },
    ),
  resumeCleanupJob: (identifier: string, jobId: string) =>
    request<CleanupJobView>(
      `/api/tenants/${encodeURIComponent(identifier)}/cleanup-jobs/${encodeURIComponent(jobId)}/resume`,
      { method: "POST" },
    ),
  setCleanupAutoAdvance: (identifier: string, jobId: string, paused: boolean) =>
    request<CleanupJobView>(
      `/api/tenants/${encodeURIComponent(identifier)}/cleanup-jobs/${encodeURIComponent(jobId)}/auto-advance`,
      { method: "POST", body: JSON.stringify({ paused }) },
    ),
  requestCleanupServiceRelease: (identifier: string) =>
    request<{ status: "REQUESTED"; requestedAt: string }>(
      `/api/tenants/${encodeURIComponent(identifier)}/cleanup-service-request`,
      { method: "POST" },
    ),
  updateTrialState: (identifier: string, input: TrialStateInput) =>
    request<TrialTenantView>(`/api/tenants/${encodeURIComponent(identifier)}/trial-state`, {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  getConnectivity: () => request<AdminConnectivityView>("/api/admin/connectivity"),
  connectVpn: (totp: string) =>
    request<AdminConnectivityView>("/api/admin/connectivity/vpn/connect", {
      method: "POST",
      body: JSON.stringify({ totp }),
    }),
  disconnectVpn: () =>
    request<AdminConnectivityView>("/api/admin/connectivity/vpn/disconnect", { method: "POST" }),
  configureMysql: (input: MysqlConfigurationInput) =>
    request<AdminConnectivityView>("/api/admin/connectivity/mysql", {
      method: "PUT",
      body: JSON.stringify(input),
    }),
  testMysql: () =>
    request<AdminConnectivityView>("/api/admin/connectivity/mysql/test", { method: "POST" }),
  synchronizeNow: () =>
    request<AdminConnectivityView>("/api/admin/connectivity/synchronizations", { method: "POST" }),
};
